import React from 'react';
import BannerAd from '../components/ibok-core/module/BannerAd';
import FrontpageSearch from '../components/FrontpageSearch';
import useAdsBannerForPage from '../hooks/useAdsBannerForPage';
import { getMostPopularBooks } from '../utils/api/bookApi';
import { getWelcomePage } from '../utils/api/pageApi';
import { truncate } from '../utils/stringUtil';
import './Frontpage.less';
/** Formats a number with norwegian thousands separators: 12345 -> "12 345" */
var formatCount = function (value) { return (value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' ')); };
var formatPrice = function (price) {
    if (price === null || price === undefined) {
        return 'Ingen priser';
    }
    if (Number(price) === 0) {
        return 'Gis bort!';
    }
    return Number(price).toLocaleString('nb-NO') + ",-";
};
var formatEdition = function (edition) { return (edition && edition > 0 ? edition + ". utgave" : 'Ukjent utgave'); };
var PopularBookPreview = function (_a) {
    var book = _a.book;
    var bookPage = "/bok/" + book.isbn;
    var title = truncate(book.title, 47);
    var authors = truncate(book.authors.join(', '), 87);
    return React.createElement("section", { className: "bookpreview" },
        React.createElement("div", { className: "bookpreview__cover" },
            React.createElement("a", { href: bookPage }, book.imageUrl && React.createElement("img", { src: book.imageUrl, alt: book.title }))),
        React.createElement("div", { className: "bookpreview__metadata" },
            React.createElement("a", { href: bookPage },
                React.createElement("h4", { className: "bookpreview__metadata__title" },
                    title,
                    " (",
                    formatEdition(book.edition),
                    ")")),
            React.createElement("p", { className: "bookpreview__metadata__authors" }, authors),
            React.createElement("p", { className: "metriccounterpreview" },
                React.createElement("span", null, "Selges brukt: "),
                React.createElement("em", null,
                    book.numberOfClassifieds,
                    " stk")),
            React.createElement("a", { href: bookPage, className: "pricepreview" },
                book.lowestPrice !== null && book.lowestPrice !== undefined && (React.createElement("span", { className: "pricepreview__label" }, "Fra ")),
                React.createElement("em", { className: "pricepreview__price" }, formatPrice(book.lowestPrice)))));
};
var HowItWorks = function () { return (React.createElement(React.Fragment, null,
    React.createElement("h2", { className: "how-it-works-header" }, "Hvordan iBok virker"),
    React.createElement("div", { className: "columns how-it-works" },
        React.createElement("section", { className: "instruction" },
            React.createElement("img", { src: "/static/img/ib_search_illustration.svg", alt: "S\u00F8k etter boken" }),
            React.createElement("h3", { className: "instruction-header" }, "S\u00F8k etter boken"),
            React.createElement("p", null, "Bruk s\u00F8kefeltet for \u00E5 finne boken du \u00F8nsker \u00E5 kj\u00F8pe eller selge.")),
        React.createElement("section", { className: "instruction" },
            React.createElement("img", { src: "/static/img/ib_find_seller_illustration.svg", alt: "Finn riktig annonse" }),
            React.createElement("h3", { className: "instruction-header" }, "Finn riktig annonse"),
            React.createElement("p", null, "P\u00E5 iBok finner du mange annonser \u00E5 velge mellom, velg den som passer for deg!")),
        React.createElement("section", { className: "instruction" },
            React.createElement("img", { src: "/static/img/ib_contact_seller_illustration.svg", alt: "Kontakt selger" }),
            React.createElement("h3", { className: "instruction-header" }, "Kontakt selger"),
            React.createElement("p", null, "Bruk v\u00E5rt kontaktskjema for \u00E5 lett komme i kontakt med selgeren. Dere avtaler selv hvordan dere g\u00E5r fram herfra."))))); };
/**
 * The frontpage, migrated from the legacy PHP view (views/welcome.php).
 *
 * Splash hero with the frontpage search bar, followed by popular books
 * from the Kotlin API, the how-it-works section and the banner slot.
 *
 * Intentionally not carried over from the legacy page:
 * - The marketing-popup mount (welcomePageWrapper / the WelcomePage
 *   stub). When the next marketing survey popup is needed, add it as a
 *   component rendered by this page.
 * - The side banner towers: the Kotlin banner API has no tower slot
 *   (see Frontpage.less for the top-banner handling instead).
 * */
var Frontpage = function () {
    var banners = useAdsBannerForPage('welcome').banners;
    var _a = React.useState(null), popularBooks = _a[0], setPopularBooks = _a[1];
    var _b = React.useState(null), activeClassifieds = _b[0], setActiveClassifieds = _b[1];
    React.useEffect(function () {
        var isMounted = true;
        getMostPopularBooks()
            .then(function (books) {
            if (isMounted) {
                setPopularBooks(books);
            }
        })
            .catch(function () {
            // The frontpage is still usable without the popular books,
            // so the section is simply left out.
        });
        return function () {
            isMounted = false;
        };
    }, []);
    React.useEffect(function () {
        var isMounted = true;
        getWelcomePage()
            .then(function (page) {
            if (isMounted) {
                setActiveClassifieds(page.statistics.activeClassifieds);
            }
        })
            .catch(function () {
            // The count is a nice-to-have and is left out on failure.
        });
        return function () {
            isMounted = false;
        };
    }, []);
    var booksWithIsbn = (popularBooks || []).filter(function (book) { return book.isbn !== null; });
    return React.createElement(React.Fragment, null,
        React.createElement("section", { className: "splash frontpage__splash", style: { backgroundImage: 'url(/static/img/forest_blurred.jpg)' } },
            React.createElement("h1", { className: "logo" },
                React.createElement("img", { src: "/static/img/ibok_logo.svg", alt: "iBok.no" })),
            React.createElement("p", { className: "slogan" }, "Norges st\u00F8rste markedsplass for kj\u00F8p og salg av brukte pensumb\u00F8ker"),
            React.createElement("div", { className: "search-box" },
                React.createElement("div", { className: "steps" },
                    React.createElement("div", { className: "step" },
                        React.createElement("p", null,
                            React.createElement("span", { className: "step-icon fas fa-search" }),
                            "S\u00F8k p\u00E5 boken du \u00F8nsker!")),
                    React.createElement("div", { className: "step" },
                        React.createElement("p", null,
                            React.createElement("span", { className: "step-icon step-icon-wide fas fa-check-square" }),
                            "Velg annonsen som passer for deg")),
                    React.createElement("div", { className: "step" },
                        React.createElement("p", null,
                            React.createElement("span", { className: "step-icon step-icon-wide fas fa-comments" }),
                            "Kontakt selgere via chatten!"))),
                React.createElement(FrontpageSearch, null)),
            React.createElement("strong", { className: "or-scan" }, "eller"),
            React.createElement("a", { className: "scan", href: "/strekkode", title: "Scan strekkode" },
                "Scan strekkode",
                React.createElement("img", { src: "/static/img/barcode.svg", alt: "Barcode" }))),
        React.createElement("div", { className: "columns frontpage__content" },
            React.createElement("div", { className: "main-content wide-content welcome-content" },
                React.createElement(BannerAd, { banners: banners, page: "welcome", size: "desktop" }),
                React.createElement(BannerAd, { banners: banners, page: "welcome", size: "mobile" }),
                React.createElement("article", null,
                    booksWithIsbn.length > 0 && (React.createElement("section", null,
                        React.createElement("h2", { className: "green-color" }, "De mest popul\u00E6re b\u00F8kene akkurat n\u00E5"),
                        activeClassifieds !== null && (React.createElement("p", null,
                            "Vi har ",
                            React.createElement("em", null,
                                formatCount(activeClassifieds),
                                " aktive annonser"),
                            " p\u00E5 ibok.no, fordelt over utallige b\u00F8ker. Dette er de mest popul\u00E6re akkurat n\u00E5.")),
                        React.createElement("div", { className: "mostpopular" }, booksWithIsbn.map(function (book) { return (React.createElement(PopularBookPreview, { key: book.id, book: book })); })))),
                    React.createElement(HowItWorks, null),
                    React.createElement("section", { className: "contact-ibok centered centered-in-container" },
                        React.createElement("h3", null, "Har du noen sp\u00F8rsm\u00E5l?"),
                        React.createElement("button", { type: "button", className: "contact-us" }, "Ta kontakt med oss"))))));
};
export default Frontpage;
